import { BitkitCheckbox, BitkitCodeSnippet, BitkitLink, BitkitList, IconArrowNortheast } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Separator } from '@chakra-ui/react/separator';
import { Text } from '@chakra-ui/react/text';

import { CiProviderId } from '@/models/CiProvider';
import { buildCachePath } from '@/utils/monolithApiRouteService';

type WarmUpCacheStepProps = {
  onWarmUpCacheCompleted: (isCompleted: boolean) => void;
  buildCacheOverViewPagePath?: string;
  /**
   * Recommended workflow name highlighted in instruction #1 when the user
   * arrived via Quick Connect.
   */
  recommendedWorkflowName?: string;
  selectedCiProvider?: CiProviderId;
};

const GitHubActionsRunSteps = () => (
  <>
    <BitkitList.Item helperText="Commit and push the workflow file you configured in the previous step. This triggers the first run.">
      Push your workflow changes.
    </BitkitList.Item>
    <BitkitList.Item helperText="Each run adds entries to the cache. Push a few commits or re-run from GitHub.">
      Run the build 2–3 more times.
    </BitkitList.Item>
  </>
);

const GenericRunSteps = ({ recommendedWorkflowName }: Pick<WarmUpCacheStepProps, 'recommendedWorkflowName'>) => (
  <>
    <BitkitList.Item>
      {recommendedWorkflowName ? (
        <>
          Run the{' '}
          <BitkitCodeSnippet variant="inline" size="md" interactive>
            {recommendedWorkflowName}
          </BitkitCodeSnippet>{' '}
          Workflow.
        </>
      ) : (
        'Run the Workflow you configured in the previous step.'
      )}
    </BitkitList.Item>
    <BitkitList.Item helperText="The cache improves with each run. You'll start seeing time savings data after the same command runs 10 times in the same project and workflow.">
      Start running your builds.
    </BitkitList.Item>
  </>
);

const WarmUpCacheStep = ({
  onWarmUpCacheCompleted,
  buildCacheOverViewPagePath = buildCachePath(),
  recommendedWorkflowName,
  selectedCiProvider,
}: WarmUpCacheStepProps) => (
  <Box display="flex" flexDirection="column" gap="24">
    <Box display="flex" flexDirection="column" gap="4">
      <Text textStyle="heading/h3" color="text/primary">
        Warm up cache
      </Text>
      <Text textStyle="body/md/regular" color="text/secondary">
        To start using Build Cache, complete following steps.
      </Text>
    </Box>
    <BitkitList variant="explainer">
      {selectedCiProvider === CiProviderId.githubActions ? (
        <GitHubActionsRunSteps />
      ) : (
        <GenericRunSteps recommendedWorkflowName={recommendedWorkflowName} />
      )}
      <BitkitList.Item helperText="The connection is successful if invocations from the build appear in the history.">
        Check{' '}
        <Text as="span" textStyle="body/lg/semibold" color="text/body">
          Cache history
        </Text>{' '}
        on the{' '}
        <BitkitLink colorVariant="purple" href={buildCacheOverViewPagePath} isExternal suffixIcon={IconArrowNortheast}>
          Build Cache overview page
        </BitkitLink>
      </BitkitList.Item>
    </BitkitList>
    <Separator />
    <Box display="flex" flexDirection="column" gap="16">
      <Text textStyle="body/lg/regular" color="text/body">
        To complete connection setup, confirm you have completed cache warming.
      </Text>
      <BitkitCheckbox
        labelText="I’ve completed cache warming."
        onCheckedChange={(details) => onWarmUpCacheCompleted(details.checked === true)}
      />
    </Box>
  </Box>
);

export default WarmUpCacheStep;
